// This flag is used between component renders to know when a button is used to switch
// a route, but will be present on the next screen, and therefore needs to be

import { useCallback, useEffect } from "react";

// automatically focused to easen keyboard navigation.
let refocusIdOnNextRender = null;

export const useRefocusOnNextRender = element => {
  const id = element?.id;

  const tryAutofocusLinkIfNeeded = useCallback(() => {
    if (!element) return;

    // If the focus has already been changed by the user during the setTimeout delay, we
    // don't want to change back the focus to our element.
    //
    // Note : When a link changes the route, and the focus is lost on the link, the focus
    // is handled back to the body element.
    if (document.activeElement !== document.body) return;

    element.focus();
  }, [element]);

  // Depending on the state of the Relay cache, the rendering of the whole page (like the
  // dataviz component) can take some time, so we use 2 tries to autofocus the link :
  // the first immediately on rendering, which works when the relay query was already
  // cached and the rendering is instant, and the second, 1 second later, which works when
  // the query wasn't cached, and the rendering of the component take some time.
  useEffect(() => {
    if (!element || !id || refocusIdOnNextRender !== id) return;

    tryAutofocusLinkIfNeeded(refocusIdOnNextRender);
    setTimeout(() => tryAutofocusLinkIfNeeded(refocusIdOnNextRender), 1000);

    refocusIdOnNextRender = null;
  }, [id, element, tryAutofocusLinkIfNeeded]);

  const refocusOnNextRender = useCallback(() => {
    if (id) refocusIdOnNextRender = id;
  }, [id]);

  return refocusOnNextRender;
};
